import { Link, graphql, useStaticQuery } from 'gatsby';
import { Accordion } from 'libs/growth-platform-brand-system-v2/src/components/Accordion';
import { Picto } from 'libs/growth-platform-brand-system-v2/src/components/Picto';
import { BrandSystemContext } from 'libs/growth-platform-brand-system-v2/src/contexts/BrandSystemContext';
import { FaqQuestion } from 'libs/growth-platform-brand-system-v2/src/layouts/Faq/FaqQuestion';
import { textBodySmall } from 'libs/growth-platform-brand-system-v2/src/styles/typography';
import {
  fontActionMini,
  fontCaption,
} from 'libs/growth-platform-brand-system-v2/src/styles/typographyNew';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import { TrackingZone } from 'libs/growth-platform-brand-system-v2/src/utils/tracking';
import { ButtonTracking } from 'libs/growth-platform-brand-system-v2/src/utils/tracking/ButtonTracking';
import React, { FC, useContext } from 'react';
import { linkResolver } from '../../utils/linkResolver';
import { Languages } from '../languages';
import ExternalLink from './ExternalLink';
import './footer.scss';
import InternalLink from './InternalLink';

interface IFooter {
  locale: 'fr' | 'en' | 'es';
  simplifiedFooter?: boolean;
  templateData?: any;
}

const prepareFooterSections = (footer: any, isHome: any, locale: any) => {
  return footer.sections.map((section: any, sectionKey: any) => {
    const faqData = {
      question: section.title,
      answer: (
        <>
          {section.items.map((item: any, itemKey: any) => (
            <div
              className={`flex flex-nowrap items-center gap-12`}
              key={`${itemKey}-${sectionKey}`}
            >
              {item.internalLink ? (
                <InternalLink
                  section={`${sectionKey}_footerLink`}
                  index={itemKey}
                  title={item.title}
                  obfuscationActivated={item.obfuscationActivated && !isHome}
                  to={linkResolver(item.internalLink, locale, item.urlParams)}
                  className={classNames(
                    'mb-12',
                    `group cursor-pointer transition-all duration-200 hover:text-blue-101`,
                  )}
                >
                  {item.title}
                </InternalLink>
              ) : (
                <ExternalLink
                  section={`${sectionKey}_footerLink`}
                  index={itemKey}
                  obfuscationActivated={item.obfuscationActivated && !isHome}
                  title={item.title}
                  href={item.externalLink || '#'}
                  target="_blank"
                  className={classNames(
                    'mb-12',
                    `group cursor-pointer transition-all duration-200 hover:text-blue-101`,
                  )}
                >
                  {item.title}
                </ExternalLink>
              )}
            </div>
          ))}
        </>
      ),
    };
    return faqData;
  });
};

const FooterAccordion = ({ question, answer, index = 0 }: any) => {
  return (
    <Accordion
      className={classNames(
        index === 0 && 'border-t',
        'border-[#DFE6ED]',
        'border-b md:border-0',
        'w-full md:w-1/2 lg:w-[calc(25%-60px)]',
      )}
      question={question}
      answer={answer}
      index={index}
    >
      <Accordion.Button className={classNames('items-start', 'py-[20px]')}>
        <FaqQuestion classNameIcon="h-[14px] w-[14px]">
          {question && (
            <div
              className={classNames(
                'flex-1 text-left',
                fontActionMini,
                'text-[#042040]',
              )}
            >
              {question}
            </div>
          )}
        </FaqQuestion>
      </Accordion.Button>
      <Accordion.Content
        animOnClose
        className={classNames('-top-16 pr-24', textBodySmall)}
      ></Accordion.Content>
    </Accordion>
  );
};
export const Footer: FC<IFooter> = ({ locale, templateData }: any) => {
  // const [hubspotScriptLoaded, setHubspotScriptLoaded] = useState<any>(false);
  const context = useContext(BrandSystemContext);
  const isHome = context?.slug === 'home';

  const data = useStaticQuery(graphql`
    query FooterQuery {
      fr: datoCmsFooter(locale: "fr") {
        ...Footer
      }
      en: datoCmsFooter(locale: "en") {
        ...Footer
      }
      es: datoCmsFooter(locale: "es") {
        ...Footer
      }
    }
  `);

  const footer = data[locale];

  const accordions = prepareFooterSections(footer, isHome, locale);
  return (
    <TrackingZone.Provider value="footer">
      <footer
        className={classNames(
          'Footer',
          fontCaption,
          'text-[#55687D]',
          'mx-16 mt-[60px] md:mt-80',
        )}
      >
        <div
          className={classNames(
            'flex flex-col flex-wrap',
            'mb-40 md:flex-row',
            'w-full max-w-[1202px] lg:mx-auto',
          )}
        >
          <div
            className={classNames('flex flex-col', 'w-full md:w-1/3 lg:w-1/5')}
          >
            <div className="flex flex-row justify-between md:flex-col">
              <div className="mb-[39px] flex flex-col items-start">
                {footer?.payfitLogo && (
                  <ButtonTracking
                    component={Link}
                    labelTracking={footer.payfitLogo.alt}
                    section="footerLink_logo"
                    className="mb-[23px] h-[39px] md:mb-[31px]"
                    to={linkResolver({ slug: 'home' }, locale)}
                  >
                    <img
                      src={footer.payfitLogo.url}
                      alt={footer.payfitLogo.alt || 'payfit logo'}
                      className="h-full w-full"
                      loading="lazy"
                      width={127}
                      height={40}
                    />
                  </ButtonTracking>
                )}
                {/* Address payfit */}
                {footer?.address && (
                  <div className="mb-[23px] whitespace-pre-line md:mb-[31px]">
                    {footer?.address}
                  </div>
                )}
                {/* Contact column */}
                <div className="flex flex-col">
                  {footer.firstSection?.[0]?.items?.map(
                    (item: any, index: number) => {
                      return (
                        <div
                          key={index}
                          className="mb-[15px] flex items-center md:mb-[11px]"
                        >
                          {item.icon && (
                            <Picto
                              name={item.icon}
                              disablePadding
                              className="mr-[9px] h-[14px] w-[14px]"
                            />
                          )}
                          {item.internalLink ? (
                            <InternalLink
                              section={`firstSection_footerLink`}
                              index={index}
                              title={item.title}
                              obfuscationActivated={
                                item.obfuscationActivated && !isHome
                              }
                              to={linkResolver(item.internalLink, locale)}
                              className={classNames(
                                `group cursor-pointer transition-all duration-200 hover:text-blue-101`,
                              )}
                            >
                              {item.title}
                            </InternalLink>
                          ) : (
                            <ExternalLink
                              section={`firstSection_footerLink`}
                              index={index}
                              obfuscationActivated={
                                item.obfuscationActivated && !isHome
                              }
                              title={item.title}
                              href={item.externalLink || '#'}
                              target="_blank"
                              className={classNames(
                                `group cursor-pointer transition-all duration-200 hover:text-blue-101`,
                              )}
                            >
                              {item.title}
                            </ExternalLink>
                          )}
                        </div>
                      );
                    },
                  )}
                  {/* Logo Grey */}
                  <div className="order-2 mt-32 flex w-full flex-wrap items-center gap-16 xl:order-none">
                    {footer?.bottomLogos?.map(
                      (bottomLogo: any, bottomLogoKey: number) => (
                        <div
                          className={classNames(
                            'max-h-40 max-w-[90px] overflow-hidden',
                          )}
                          key={bottomLogoKey}
                        >
                          <img
                            src={bottomLogo?.url}
                            alt={bottomLogo?.alt || 'bottom logo'}
                            className="h-full w-full"
                            loading="lazy"
                            width={bottomLogo?.width}
                            height={bottomLogo?.height}
                          />
                        </div>
                      ),
                    )}
                  </div>
                  <div
                    className={
                      'order-1 block md:mt-[23px] md:hidden xl:order-none'
                    }
                  >
                    <Languages templateData={templateData} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={classNames(
              'flex flex-1 flex-wrap md:gap-y-[46px] lg:gap-x-[60px]',
            )}
          >
            {accordions?.map((accordion: any, index: number) => (
              <FooterAccordion key={index} {...{ ...accordion, index }} />
            ))}
          </div>
        </div>
        <div
          className={classNames(
            'flex w-full flex-wrap items-center md:py-[37px]',
            'my-40 justify-between gap-y-[30px] border-[#DFE6ED] md:gap-y-[24px] md:border-t',
            'mb-[20px]',
            'w-full max-w-[1202px] lg:mx-auto',
          )}
        >
          {footer?.subsectionItems && (
            <>
              <div
                className={classNames(
                  'flex flex-wrap gap-12 md:justify-between',
                  'w-full xl:w-auto',
                )}
              >
                <span className={classNames(textBodySmall)}>© PayFit</span>
                {footer.subsectionItems.map((item: any, itemKey: any) => {
                  if (item.internalLink) {
                    return (
                      <InternalLink
                        section={`${itemKey}_footerLink_subsectionItems`}
                        index={itemKey}
                        title={item.title}
                        obfuscationActivated={
                          item.obfuscationActivated && !isHome
                        }
                        key={itemKey}
                        to={linkResolver(item.internalLink, locale)}
                        className={classNames(
                          textBodySmall,
                          `cursor-pointer transition-all duration-200 hover:text-blue-101`,
                        )}
                      >
                        {item.title}
                      </InternalLink>
                    );
                  }
                  if (item.externalLink) {
                    return (
                      <ExternalLink
                        section={`${itemKey}_footerLink_subsectionItems`}
                        index={itemKey}
                        title={item.title}
                        obfuscationActivated={
                          item.obfuscationActivated && !isHome
                        }
                        key={itemKey}
                        href={item.externalLink || '#'}
                        target="_blank"
                        className={classNames(
                          textBodySmall,
                          `cursor-pointer transition-all duration-200 hover:text-blue-101`,
                        )}
                      >
                        {item.title}
                      </ExternalLink>
                    );
                  }
                  if (item.urlParams) {
                    return (
                      <ButtonTracking
                        component={'a'}
                        section={`${itemKey}_footerLink_subsectionItems`}
                        labelTracking={item.title}
                        index={itemKey}
                        className={classNames(
                          textBodySmall,
                          `cursor-pointer transition-all duration-200 hover:text-blue-101`,
                        )}
                        key={itemKey}
                        href={`?${item.urlParams}`}
                      >
                        {item.title}
                      </ButtonTracking>
                    );
                  }
                  return <span key={itemKey}>{item.title}</span>;
                })}
              </div>
              <div className="flex flex-wrap items-center gap-8">
                {footer?.socialNetworks && (
                  <div className="flex space-x-16">
                    {footer.socialNetworks.map(
                      (socialNetwork: any, socialNetworkKey: any) => (
                        <ExternalLink
                          section="footer_social"
                          title={socialNetwork.icon.alt}
                          aria-label={socialNetwork.icon.alt}
                          index={socialNetworkKey}
                          obfuscationActivated={!isHome}
                          className="h-20 cursor-pointer transition-opacity duration-200 hover:opacity-70"
                          href={socialNetwork.url}
                          target="_blank"
                          key={socialNetworkKey}
                        >
                          <img
                            src={socialNetwork.icon.url}
                            alt={
                              socialNetwork.icon.alt || 'social network icon'
                            }
                            className="h-full w-full"
                            loading="lazy"
                            width={20}
                            height={20}
                          />
                        </ExternalLink>
                      ),
                    )}
                  </div>
                )}
              </div>
              <div className={'hidden md:block'}>
                <Languages templateData={templateData} />
              </div>
            </>
          )}
        </div>
      </footer>
    </TrackingZone.Provider>
  );
};
